import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { ApiErrors_ENUM } from "@models/mood/helpers/errors";
import { TooManyDownloadsDialogComponent } from "./too-many-downloads-dialog/too-many-downloads-dialog.component";
import { AuthService } from "../core/auth/auth.service";
import { DatasetMetainfoDialogComponent, DatasetMetainfoDialogData } from "./dataset-metainfo-dialog/dataset-metainfo-dialog.component";
import { Router } from "@angular/router";
import { SOURCE_TYPE } from "../core/layers/base-layer.class";
import { FOURIER_EXPLANATION_TEXT, TIMESERIES_EXPLANATION_TEXT } from "../constant";
import { COVARIATE_DATASET_TYPE_ENUM } from "@models/mood/covariates/dataset";
import { IDatasetTreeNode, ITreeNode } from "../core/datasets/tree.interface";
import { TimeseriesDataExtractionComponent, TimeseriesDataExtractionComponentData } from "../dialogs/extraction/timeseries-data-extraction-dialog.component";

@Component({
  selector: 'mood-dataset-node',
  templateUrl: './dataset-node.component.html',
  styleUrls: ['./dataset-node.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatasetNodeComponent implements OnInit {
  @Input() node!: ITreeNode;
  @Input() icon?: string;
  @Input() showMenu: boolean = true;
  @Input() expanded: boolean = false;

  @Output() datasetClicked = new EventEmitter();

  constructor(private _authService: AuthService, private _http: HttpClient, private _dialog: MatDialog, private _router: Router) { }

  public get canEditDataset() {
    return this._authService.userHasAdminRole;
  }

  public get hasMetainfoAvailable() {
    return this.treeNodeAsDataset.metainfo_id
  }

  public datasetTooltip!: string;

  ngOnInit(): void {
    this.datasetTooltip = this.treeNodeAsDataset.tooltip ?? "";

    if (this.isTimeSeriesNode) {
      this.datasetTooltip += `\n\n${TIMESERIES_EXPLANATION_TEXT}`;
    }

    if (this.isFourierNode) {
      this.datasetTooltip += `\n\n${FOURIER_EXPLANATION_TEXT}`;
    }
  }

  public get treeNodeAsDataset() {
    return this.node as IDatasetTreeNode;
  }

  navigateToDatasetEditPage() {
    this._router.navigate(['/', 'admin'], {
      queryParams: { type: SOURCE_TYPE.COVARIATE, id: this.treeNodeAsDataset.dataset_id }
    });
  }

  openDatasetMetainfoDialog(event: MouseEvent) {
    event.stopPropagation();

    this._dialog.open(DatasetMetainfoDialogComponent, {
      minWidth: '800px',
      maxWidth: 'min(80vw, 1600px)',
      data: {
        dataset_id: this.treeNodeAsDataset.dataset_id,
        metainfo_id: this.treeNodeAsDataset.metainfo_id,
        dataset_name: this.node.label
      } as DatasetMetainfoDialogData
    });
  }

  async downloadDataset() {
    await this._http.get<{ url: string }>(`/api/dataset/${this.treeNodeAsDataset.dataset_id}/download`).subscribe({
      next: data => {
        window.open(data.url, "_blank");
      },
      error: err => {
        if (err.error && err.error.code === ApiErrors_ENUM.TooManyDownloads) {
          this._dialog.open(TooManyDownloadsDialogComponent);
        }
      }
    })
  }

  extractDataset() {
    this._dialog.open(TimeseriesDataExtractionComponent, {
      maxWidth: 'min(80vw, 1200px)',
      // minWidth: '1200px',
      minWidth: '400px',
      disableClose: true,
      maxHeight: '90vh',
      data: <TimeseriesDataExtractionComponentData>{
        dataset_id: this.treeNodeAsDataset.dataset_id
      }
    });
  }

  buttonOrNameClicked() {
    this.datasetClicked.emit();
  }

  authenticated = this._authService._loggedIn$;

  public get isActualDataset() {
    return this.node.type === "DATASET" && this.treeNodeAsDataset.original_dataset_type && this.treeNodeAsDataset.original_dataset_type !== COVARIATE_DATASET_TYPE_ENUM.VECTOR;
  }

  public get unvalidated() {
    return this.isActualDataset && this.treeNodeAsDataset.unvalidated;
  }

  public get isTimeSeriesNode() {
    return this.treeNodeAsDataset.original_dataset_type === COVARIATE_DATASET_TYPE_ENUM.TIMESERIES;
  }

  public get isFourierNode() {
    return this.treeNodeAsDataset.original_dataset_type === COVARIATE_DATASET_TYPE_ENUM.FOURIER;
  }

  public get tooltipDisabled() {
    return this._authService.userIsExpert;
  }
}
